import { Icon } from '@iconify/react';
import { useHasMenu } from '../../../auth/access';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { logError } from '../../../utils/logger';
import { Card, SectionTitle } from '../../shared/components';
import DayEditDialog, { DayTarget } from '../components/DayEditDialog';
import ImportCsvDialog from '../components/ImportCsvDialog';
import MonthPicker, { thisMonth, YearMonth } from '../components/MonthPicker';
import {
  attendanceApi,
  AttendanceRegister,
  DAY_STATUS,
  days,
  hhmm,
  localDate,
  minutesLabel,
  monthLabel,
  RegisterCell,
  statusStyle,
} from '../payroll.api';

const SUMMARY_COLS: { key: keyof AttendanceRegister['employees'][number]['summary']; label: string }[] = [
  { key: 'present', label: 'P' },
  { key: 'halfDays', label: 'H' },
  { key: 'paidLeave', label: 'PL' },
  { key: 'unpaidLeave', label: 'UL' },
  { key: 'absent', label: 'A' },
  { key: 'lateMarks', label: 'Late' },
  { key: 'lopDays', label: 'LOP' },
];

const cellSx = {
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
  px: 0.5,
  py: 0.5,
  textAlign: 'center' as const,
  fontSize: 12,
  whiteSpace: 'nowrap' as const,
};

/**
 * The month for every employee, one coloured code per day. A click on a day corrects it; the
 * biometric import fills many days at once.
 */
export default function AttendanceRegisterPage() {
  const [ym, setYm] = useState<YearMonth>(thisMonth());
  const [register, setRegister] = useState<AttendanceRegister | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DayTarget | null>(null);
  const [importing, setImporting] = useState(false);
  // Correcting days and importing are the register's EDIT action (V30).
  const canEdit = useHasMenu('hr.attendance-register', 'EDIT');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    attendanceApi
      .register(ym.year, ym.month)
      .then(setRegister)
      .catch((e) => {
        setRegister(null);
        setError(logError({ message: 'Could not load the attendance register', error: e, code: 'ATTENDANCE_REGISTER_FAILED' }));
      })
      .finally(() => setLoading(false));
  }, [ym]);

  useEffect(load, [load]);

  const today = localDate();

  return (
    <Stack gap={3}>
      <SectionTitle
        title='ATTENDANCE REGISTER'
        action={
          canEdit ? (
            <Button variant='outlined' startIcon={<Icon icon='mdi:file-upload-outline' />} onClick={() => setImporting(true)}>
              Import biometric CSV
            </Button>
          ) : undefined
        }
      />

      <Card title={monthLabel(ym.year, ym.month).toUpperCase()} action={<MonthPicker value={ym} onChange={setYm} />}>
        {loading && <LinearProgress />}
        {error && <Alert severity='error'>{error}</Alert>}
        {register && register.employees.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No employees on the books this month.
          </Typography>
        )}
        {register && register.employees.length > 0 && (
          <Box sx={{ overflowX: 'auto', maxHeight: '70vh', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box component='table' sx={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%' }}>
              <thead>
                <tr>
                  <Box
                    component='th'
                    sx={{ ...cellSx, position: 'sticky', left: 0, top: 0, zIndex: 3, bgcolor: 'background.paper', textAlign: 'left', minWidth: 180 }}
                  >
                    Employee
                  </Box>
                  {register.days.map((d) => {
                    const off = d.dayKind !== 'WORKING';
                    return (
                      <Tooltip key={d.date} title={d.holidayName || (d.dayKind === 'WEEKLY_OFF' ? 'Weekly off' : '')}>
                        <Box
                          component='th'
                          sx={{
                            ...cellSx,
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            minWidth: 34,
                            bgcolor: off ? 'grey.200' : 'background.paper',
                            outline: d.date === today ? '2px solid' : 'none',
                            outlineColor: 'primary.main',
                          }}
                        >
                          <div>{Number(d.date.slice(8, 10))}</div>
                          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10 }}>
                            {d.weekday?.slice(0, 2)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {SUMMARY_COLS.map((c) => (
                    <Box key={c.key} component='th' sx={{ ...cellSx, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'grey.100', minWidth: 40 }}>
                      {c.label}
                    </Box>
                  ))}
                </tr>
              </thead>
              <tbody>
                {register.employees.map((emp) => {
                  const byDate = new Map<string, RegisterCell>(emp.days.map((c) => [c.date, c]));
                  return (
                    <tr key={emp.employeeId}>
                      <Box
                        component='td'
                        sx={{ ...cellSx, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', textAlign: 'left' }}
                      >
                        <Typography variant='body2' fontWeight={600} noWrap>
                          {emp.name}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' noWrap>
                          {[emp.code, emp.department].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      {register.days.map((d) => {
                        const cell = byDate.get(d.date);
                        if (!cell) return <Box key={d.date} component='td' sx={cellSx} />;
                        const s = statusStyle(cell.status);
                        const editable = canEdit && cell.status !== 'NOT_EMPLOYED';
                        const tip = [
                          s.label,
                          d.holidayName,
                          cell.punchIn ? `In ${hhmm(cell.punchIn)}` : null,
                          cell.punchOut ? `Out ${hhmm(cell.punchOut)}` : null,
                          cell.workedMinutes ? minutesLabel(cell.workedMinutes) : null,
                          cell.isLate ? 'Late' : null,
                          cell.override ? `Override: ${statusStyle(cell.override).label}` : null,
                          editable ? 'Click to correct' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <Tooltip key={d.date} title={tip}>
                            <Box
                              component='td'
                              onClick={editable ? () => setEditing({ employeeId: emp.employeeId, employeeName: emp.name, cell }) : undefined}
                              sx={{
                                ...cellSx,
                                bgcolor: s.bg,
                                color: s.fg,
                                fontWeight: 700,
                                cursor: editable ? 'pointer' : 'default',
                                position: 'relative',
                                '&:hover': editable ? { filter: 'brightness(0.95)' } : undefined,
                              }}
                            >
                              {s.short}
                              {cell.isLate && (
                                <Box
                                  component='span'
                                  sx={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', bgcolor: '#EA580C' }}
                                />
                              )}
                              {cell.override && (
                                <Box
                                  component='span'
                                  sx={{ position: 'absolute', bottom: 2, right: 2, width: 5, height: 5, borderRadius: '50%', bgcolor: '#1D4ED8' }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {SUMMARY_COLS.map((c) => (
                        <Box key={c.key} component='td' sx={{ ...cellSx, bgcolor: 'grey.50', fontWeight: 600 }}>
                          {days(emp.summary[c.key])}
                        </Box>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
        <Stack direction='row' flexWrap='wrap' gap={1.5}>
          {Object.entries(DAY_STATUS)
            .filter(([k]) => k !== 'UPCOMING' && k !== 'IN_PROGRESS')
            .map(([k, s]) => (
              <Stack key={k} direction='row' alignItems='center' gap={0.5}>
                <Box sx={{ px: 0.5, minWidth: 22, textAlign: 'center', borderRadius: 0.5, bgcolor: s.bg, color: s.fg, fontSize: 11, fontWeight: 700 }}>
                  {s.short}
                </Box>
                <Typography variant='caption'>{s.label}</Typography>
              </Stack>
            ))}
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#EA580C' }} />
            <Typography variant='caption'>Late</Typography>
          </Stack>
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1D4ED8' }} />
            <Typography variant='caption'>Set by HR</Typography>
          </Stack>
        </Stack>
      </Card>

      <DayEditDialog target={editing} onClose={() => setEditing(null)} onSaved={load} />
      <ImportCsvDialog open={importing} onClose={() => setImporting(false)} onImported={load} />
    </Stack>
  );
}
